import { type ReactElement, useMemo, useState } from 'react';
import { type MapConfigView, type RouteAnalysisView, type RouteSegmentView, type WireLatLng } from '@vello/api-client';
import { formatDistance, formatDuration } from '@vello/format';
import { AUTO_REFRESH_MS, Button, EmptyState, ListTable, Notice, Panel, type Column, InlineRow, Text, Row, Stack, useAutoRefresh } from '@vello/web-theme';
import { liveMapAuthority, tileOverride } from '../../src/index';
import { MapCanvas, type MapLine } from './map-canvas';
import { useAdmin } from '../session';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
const toLatLng = (p: WireLatLng): { latitude: number; longitude: number } => ({ latitude: p.latitude, longitude: p.longitude });

interface LineRow {
  readonly id: 'toPickup' | 'estimated' | 'executed';
  readonly distanceMeters: number | undefined;
  readonly durationSeconds: number | undefined;
  readonly samples: number | undefined;
  readonly missing: boolean;
}

export function RouteAnalysisPanel({ rideId, marketId }: { readonly rideId: string; readonly marketId: string }): ReactElement | null {
  const { runtime, grants } = useAdmin();
  const { t, lang } = useT();
  const { fail, v, refusal: refusalOf } = useVocab();
  const authority = liveMapAuthority(grants);
  const [view, setView] = useState<RouteAnalysisView | undefined>(undefined);
  const [config, setConfig] = useState<MapConfigView | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);

  const ctx = { locale: lang, timeZone: 'UTC', measurement: 'metric' } as const;

  /** `quiet` is the background re-read of a ride still in progress: it keeps what is on screen (no busy flash, a failed tick changes nothing). */
  const analyze = async (quiet = false): Promise<void> => {
    if (!quiet) {
      setBusy(true);
      setError(undefined);
    }
    const [analysis, cfg] = await Promise.all([runtime.ops.routeAnalysis(rideId), runtime.ops.mapConfig(marketId)]);
    if (!quiet) setBusy(false);
    if (analysis.ok) setView(analysis.value);
    else if (quiet) return;
    else {
      setView(undefined);
      setError(refusalOf('refusal.admin.live_map', analysis.error) ?? t('lmap.err.generic', { code: fail(analysis.error) }));
    }
    // The map config needs `ops:view`: a ride-support operator without it still reads the numbers.
    setConfig(cfg.ok ? cfg.value : undefined);
  };

  // The first analysis is the operator's own query; while the ride is still going (the analysis is not final) it re-reads by itself.
  useAutoRefresh(() => analyze(true), { intervalMs: AUTO_REFRESH_MS.live, enabled: authority.mayAnalyze && view !== undefined && !view.analysis.final });

  const a = view?.analysis;
  const lines = useMemo<readonly MapLine[]>(() => {
    if (a === undefined) return [];
    const out: MapLine[] = [];
    if (a.toPickup !== undefined && a.toPickup.points.length > 1) out.push({ id: 'toPickup', points: a.toPickup.points.map(toLatLng), colorVar: 'var(--color-warning)', label: v('ride.route_analysis_line', 'toPickup') });
    if (a.estimated.points.length > 1) out.push({ id: 'estimated', points: a.estimated.points.map(toLatLng), colorVar: 'var(--color-text-tertiary)', dashed: true, label: v('ride.route_analysis_line', 'estimated') });
    if (a.executed !== undefined && a.executed.points.length > 1) out.push({ id: 'executed', points: a.executed.points.map(toLatLng), colorVar: 'var(--color-action-primary)', label: v('ride.route_analysis_line', 'executed') });
    return out;
  }, [a, t]);
  // D800: the quoted route runs from the pickup to the destination - its ends carry the Design System's markers.
  const routeEnds = useMemo(() => {
    const pts = a?.estimated.points ?? [];
    const first = pts[0];
    const last = pts[pts.length - 1];
    return first !== undefined && last !== undefined && pts.length > 1 ? { pickup: toLatLng(first), dropoff: toLatLng(last) } : undefined;
  }, [a]);

  if (!authority.mayAnalyze) return null;

  const segment = (s: RouteSegmentView | undefined, id: 'toPickup' | 'executed'): LineRow => ({ id, distanceMeters: s?.distanceMeters, durationSeconds: s?.durationSeconds, samples: s?.sampleCount, missing: s === undefined });
  const rows: readonly LineRow[] =
    a === undefined
      ? []
      : [
          segment(a.toPickup, 'toPickup'),
          { id: 'estimated', distanceMeters: a.estimated.quotedDistanceMeters ?? a.estimated.straightLineMeters, durationSeconds: a.estimated.quotedDurationSeconds, samples: undefined, missing: false },
          segment(a.executed, 'executed'),
        ];

  const columns: readonly Column<LineRow>[] = [
    {
      key: 'line',
      header: t('lran.col.line'),
      width: 'minmax(180px, 1.4fr)',
      render: (r) => (
        <InlineRow gap={2} align="center">
          <span aria-hidden="true" className="vello-admin-a-linekey" data-kind={r.id} />
          <strong>{v('ride.route_analysis_line', r.id)}</strong>
        </InlineRow>
      ),
    },
    { key: 'distance', header: t('lran.col.distance'), width: 'minmax(110px, 0.8fr)', render: (r) => (r.missing || r.distanceMeters === undefined ? <Text role="caption" tone="secondary">{t('lran.missing')}</Text> : <span className="tnum">{formatDistance(r.distanceMeters, ctx)}</span>) },
    { key: 'duration', header: t('lran.col.duration'), width: 'minmax(110px, 0.8fr)', render: (r) => (r.durationSeconds === undefined ? <Text role="caption" tone="secondary">—</Text> : <span className="tnum">{formatDuration(r.durationSeconds, ctx)}</span>) },
    { key: 'samples', header: t('lran.col.samples'), width: 'minmax(90px, 0.6fr)', render: (r) => (r.samples === undefined ? <Text role="caption" tone="secondary">—</Text> : <span className="tnum">{new Intl.NumberFormat(lang).format(r.samples)}</span>) },
  ];

  const drawable = config !== undefined && lines.length > 0;

  return (
    <Panel><Stack gap={3}>
      <Row gap={3} align="center" justify="between" wrap>
        <div>
          <Text as="h2" role="title">{t('lran.title')}</Text>
          <Text as="p" role="caption" tone="secondary">{t('lran.subtitle')}</Text>
        </div>
        {view === undefined && <Button variant="secondary" icon="mapPin" disabled={busy} onClick={() => void analyze()}>{t('lran.analyze')}</Button>}
      </Row>

      {error !== undefined && <Notice tone="danger">{error}</Notice>}

      {a !== undefined && (
        <>
          <ListTable columns={columns} rows={rows} rowKey={(r) => r.id} empty={<EmptyState title={t('lran.empty')} />} minWidth={560} />
          <Text as="p" role="caption" tone="secondary">
            {a.executedToQuotedRatio !== undefined ? t('lran.ratio', { value: new Intl.NumberFormat(lang, { style: 'percent', maximumFractionDigits: 0 }).format(a.executedToQuotedRatio) }) : t('lran.ratio.unavailable')}
            {a.final ? '' : ` ${t('lran.notFinal')}`}
          </Text>
          {drawable ? (
            <MapCanvas
              tiles={tileOverride(config?.tiles)}
              center={config?.market.center}
              points={[]}
              lines={lines}
              {...(routeEnds !== undefined ? { routeEnds } : {})}
              ariaLabel={t('lran.map.label')}
              pointLabel={() => ''}
            />
          ) : (
            <Notice tone="neutral">{t(lines.length === 0 ? 'lran.noLines' : 'lran.noMap')}</Notice>
          )}
        </>
      )}
    </Stack></Panel>
  );
}
